import { Building2, Leaf, LucideIcon, Server, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { cn } from '../../lib/utils';
import { services } from '../../services';

interface PortfolioAllocationProps {
  className?: string;
}

interface PortfolioData {
  name: string;
  value: number;
  color: string;
  icon: string;
}

const iconMap: Record<string, LucideIcon> = {
  Building2,
  Zap,
  Server,
  Leaf,
};

const getCategoryColor = (categoryName: string): string => {
  const categoryColors: Record<string, string> = {
    REAL_ESTATE: 'hsl(200, 80%, 55%)',
    CARBON_CREDITS: 'hsl(142, 72%, 45%)',
    RENEWABLES: 'hsl(38, 92%, 50%)',
    DATA_CENTRES: 'hsl(215, 12%, 55%)',
    'Real Estate': 'hsl(200, 80%, 55%)',
    'Carbon Credits': 'hsl(142, 72%, 45%)',
    Renewables: 'hsl(38, 92%, 50%)',
    'Data Centres': 'hsl(215, 12%, 55%)',
    Energy: 'hsl(38, 92%, 50%)',
    Mining: 'hsl(215, 12%, 55%)',
  };

  return categoryColors[categoryName] || 'hsl(200, 80%, 55%)';
};

export function PortfolioAllocation({ className }: PortfolioAllocationProps) {
  const [portfolioData, setPortfolioData] = useState<PortfolioData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolioAllocation();
  }, []);

  const fetchPortfolioAllocation = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: PortfolioData[];
      }>('/admin/dashboard/portfolio-allocation');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        const themedData = response.data.data.map((item: PortfolioData) => ({
          ...item,
          color: getCategoryColor(item.name),
        }));
        setPortfolioData(themedData);
      } else if (response.status === 401) {
        console.error('Authentication required for portfolio allocation');
        setPortfolioData([]);
      } else if (response.status === 403) {
        console.error('Admin access required for portfolio allocation');
        setPortfolioData([]);
      } else {
        setPortfolioData([]);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for portfolio allocation');
        setPortfolioData([]);
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for portfolio allocation');
        setPortfolioData([]);
      } else {
        console.error('Error fetching portfolio allocation:', error);
        setPortfolioData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const displayData = portfolioData.length > 0 ? portfolioData : [];

  return (
    <div className={cn('glass-card rounded-xl p-6 animate-slide-up', className)}>
      <h3 className='text-lg font-semibold text-foreground mb-6'>Portfolio Allocation</h3>
      {loading ? (
        <div className='flex items-center justify-center h-[200px] text-muted-foreground'>
          <p>Loading portfolio data...</p>
        </div>
      ) : displayData.length === 0 ? (
        <div className='flex items-center justify-center h-[200px] text-muted-foreground'>
          <p>No portfolio data available</p>
        </div>
      ) : (
        <div className='flex items-center gap-6'>
          <div className='w-32 h-32'>
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie
                  data={displayData}
                  cx='50%'
                  cy='50%'
                  innerRadius={35}
                  outerRadius={55}
                  paddingAngle={3}
                  dataKey='value'
                  strokeWidth={0}
                >
                  {displayData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(220, 50%, 8%)',
                    border: '1px solid hsl(220, 30%, 14%)',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`${value}%`, 'Allocation']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className='flex-1 space-y-3'>
            {displayData.map((item) => {
              const IconComponent = iconMap[item.icon] || Building2;
              return (
                <div key={item.name} className='flex items-center gap-3'>
                  <div className='w-3 h-3 rounded-full' style={{ backgroundColor: item.color }} />
                  <div className='flex items-center gap-2 flex-1'>
                    <IconComponent className='w-4 h-4 text-muted-foreground' />
                    <span className='text-sm text-foreground'>{item.name}</span>
                  </div>
                  <span className='text-sm font-semibold text-foreground'>{item.value}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
